/* LEGEND — Entry marks: the colour rule, the 5m / 15m ranges, MV-5's turning point and the zones from his record.
   components/EntryMarksLayer.tsx draws them. Ported from the root legend/entrymarks.jsx (emRead → readMarks,
   emRanges → rangesFrom, emPivot → pivotFrom) and legend/data.js (ENTRY_RECORD, ENTRY_ZONES).

   The marks are measured the way the trade journal measured his trades, so a colour on the chart means what his
   record says:
   - the 5-minute range: the low–high of the last 5 minutes (journal-builder facts.ts `posIn5`: the prints in the
     5 minutes before the first fill) — here the 1-minute bars of the last 5 minutes, the forming one included; the
     15-minute range the same way (facts.ts `posIn15`, recorded from 2026-10-22);
   - the third: top from 2/3 of the range, middle from 1/3, else bottom (realtime journal-pages.ts `entryRecord`
     buckets his trades the same way);
   - the turning point: the setup the journal judges `late-vs-pivot` against (journal-builder mv5.ts) — MV-5's most
     confident active (forming or confirmed) higher low, selling-stopped or base for a long, lower high for a short;
     its pivot (`facts.pivot`), else the band's edge on the class's side (the low for a long, the high for a short);
   - chasing: more than 1 ATR past it (mv5.ts LATE_PIVOT_ATR), the ATR the 1-minute ATR14 — the mean true range of the
     last 14 closed 1-minute bars (levels.ts atr14Before). Until 2026-10-22 it was 2 % of the price; his own trades
     moved it (a flat 2 % is noise on a fast name and a big move on a slow one). No ATR yet → the distance is shown in
     %, and nothing is judged chasing.

   The colour (the last-price tag): red in a bad third of the 5-minute range or past the chase line; green in a good
   third and not past it; amber in a mixed third. The 15-minute bar is context: shaded the same way, never in the tag.

   The day picture (2026-10-23): two more bars, context only (never in the tag) —
   - 4H: the low–high of the last 4 hours (the 1-minute bars of the last 240 minutes); shaded from his 4-hour record
     (`posIn240`, recorded from 10-23) once there is one, grey until then;
   - D: the day so far, 04:00 ET to now (journal-builder stages.ts `dayStart`; the journal's `hodSoFar` / `lodSoFar`),
     shaded from his record by the day's third. The price tag's line ends with the day: "the day's bottom third, 49%
     off its 5.87 high".

   A third's zone comes from his trades there (`zonesOf`): fewer than 5 trades, or within $5 a trade of even → mixed;
   making money and won at least half → good; losing and won at most half → bad; else mixed.

   No imports beyond lib and no DOM types: lib/__tests__ compile this under the root jest config. */
import { etWhen, readState } from './vision-read';
import { sessionStartMs } from './bins';

export type EmSide = 'long' | 'short';
/** 'none': no record for that bar yet (drawn grey) */
export type EmZone = 'good' | 'mid' | 'bad' | 'none';
/** one third of his record: [trades, net $, % won] */
export type EmCell = [number, number, number];

export interface EmRange { lo: number; hi: number; closed?: { lo: number; hi: number } }
export interface EmRanges { '5m': EmRange; '15m'?: EmRange; '4h'?: EmRange; D?: EmRange }
export interface EmPivot { price: number; setup: string; tf: string; p: number | null; stale?: boolean; asOf?: string }
export interface EntryMarks {
  side: EmSide;
  price: number | null;
  ranges: EmRanges | null;
  zones: Record<EmSide, EmZone[]>;
  /** the day bar's thirds (his record by the day's third) and the 4-hour bar's ('none' without a record) */
  zonesDay?: Record<EmSide, EmZone[]>;
  zones4h?: Record<EmSide, EmZone[]>;
  pivot: EmPivot | null;
  /** the 1-minute ATR14 (atrFrom); null before there are bars enough */
  atr: number | null;
  /** chasing: more than this many ATR past the turning point */
  chaseAtr: number;
  tag?: EmZone;
  reason?: string;
}
export interface EmRead {
  tag: EmZone; third: number; zone: EmZone;
  /** how far past the turning point, % of it and in ATR (2 dp, as the journal rounds); + = past it in the side's direction */
  past: number | null; pastAtr: number | null;
  chased: boolean; reason: string;
}
export interface EntryRecord {
  /** the sessions it covers, e.g. "Oct 20–22" */
  span: string;
  trades: number;
  /** the first session with his 15-minute position on file (null: none yet) */
  since15m: string | null;
  /** by third, top → middle → bottom */
  short: Array<EmCell | null>;
  long: Array<EmCell | null>;
  /** more than CHASE_ATR past the turning point */
  chase: { short: EmCell | null; long: EmCell | null };
  /** by the day's third (04:00 ET to the entry) — every card has it */
  day: { short: Array<EmCell | null>; long: Array<EmCell | null> } | null;
  /** by the 4 hours' third, and the first session with it on file */
  r240: { short: Array<EmCell | null>; long: Array<EmCell | null> } | null;
  since240: string | null;
}
export interface EmBar { time: number; high: number; low: number; close?: number }

/** more than this many ATR past the turning point is chasing (journal-builder mv5.ts LATE_PIVOT_ATR) */
export const CHASE_ATR = 1;
export const EM_THIRD = ['Top', 'Middle', 'Bottom'];
/** a third needs this many trades before it is good or bad */
export const ZONE_MIN_TRADES = 5;
/** within this many dollars a trade of even, a third is mixed */
export const ZONE_EVEN_USD = 5;

/** His record until the journal's feed answers (62 trades, Oct 20–22 — the Entry Marks brief). */
export const ENTRY_RECORD: EntryRecord = {
  span: 'Oct 20–22', trades: 62, since15m: null,
  short: [[18, 182, 78], [8, 75, 75], [5, -241, 20]],
  long: [[15, 103, 73], [8, -317, 38], [7, -72, 43]],
  chase: { short: [6, -205, 33], long: [10, -170, 40] },   // more than 1 ATR past (the 10-22 re-score of his trades)
  // the day's third, 78 trades of 10-20..23 (the 10-23 check)
  day: { short: [[31, 112, 68], [4, 167, 100], [6, -177, 67]], long: [[11, -66, 55], [12, 209, 92], [14, -435, 29]] },
  r240: null, since240: null,
};

export const emPx = (v: number | null | undefined): string => (v == null ? '—' : v < 1 ? v.toFixed(4) : v.toFixed(2));
export const emPct = (v: number): string => (v >= 3 ? Math.round(v) : +v.toFixed(1)) + '%';
export const emUsd = (v: number): string => (v < 0 ? '−$' : '+$') + Math.abs(v).toLocaleString('en-US');
/** % past the turning point, 2 dp — the journal rounds the gap before it compares it (mv5.ts `fin(gap, 2)`) */
const gapPct = (side: EmSide, price: number, pivot: number): number => Math.round((side === 'long' ? 1 : -1) * (price / pivot - 1) * 100 * 100) / 100;
/** the same in ATR, 2 dp (mv5.ts `pivotGapAtr`) */
const gapAtr = (side: EmSide, price: number, pivot: number, atr: number): number => Math.round((side === 'long' ? 1 : -1) * (price - pivot) / atr * 100) / 100;
/** "0.6" / "1.4" / "12" — a distance in ATR as the chart says it */
export const emAtr = (v: number): string => String(v >= 10 ? Math.round(v) : +v.toFixed(1));

/** The 1-minute ATR14 the journal judges chasing by (journal-builder levels.ts atr14Before): the mean true range of
 *  the last 14 closed 1-minute bars (15 bars: each range reaches back to the bar before's close). A bar is closed once
 *  its minute has passed `now`; fewer than 2 closed bars → null. */
export function atrFrom(bars: readonly EmBar[] | null | undefined, now: number): number | null {
  if (!bars || bars.length < 2) return null;
  let end = bars.length;
  while (end > 0 && bars[end - 1]!.time * 1000 + 60_000 > now) end--;
  const done = bars.slice(Math.max(0, end - 15), end);
  if (done.length < 2) return null;
  let s = 0;
  for (let i = 1; i < done.length; i++) {
    const b = done[i]!, pc = done[i - 1]!.close;
    s += pc == null ? b.high - b.low : Math.max(b.high - b.low, Math.abs(b.high - pc), Math.abs(b.low - pc));
  }
  const atr = s / (done.length - 1);
  return atr > 0 ? atr : null;
}

/** the third of a range a price sits in: 0 top, 1 middle, 2 bottom (outside the range: the nearer end's) */
export function thirdOf(pos: number): number {
  return pos >= 2 / 3 ? 0 : pos >= 1 / 3 ? 1 : 2;
}

/** A third's zone from his trades there (see the header). */
export function zoneOf(c: EmCell | null | undefined): EmZone {
  if (!c || !(c[0] >= ZONE_MIN_TRADES)) return 'mid';
  const [n, net, won] = c;
  const each = net / n;
  if (Math.abs(each) < ZONE_EVEN_USD) return 'mid';
  if (each > 0 && won >= 50) return 'good';
  if (each < 0 && won <= 50) return 'bad';
  return 'mid';
}

export function zonesOf(rec: EntryRecord): Record<EmSide, EmZone[]> {
  return { short: [0, 1, 2].map((k) => zoneOf(rec.short[k])), long: [0, 1, 2].map((k) => zoneOf(rec.long[k])) };
}

/** A context bar's thirds from its record; no trades on file for a side at all → 'none' (grey). */
function zonesOfCells(cells: { short: Array<EmCell | null>; long: Array<EmCell | null> } | null): Record<EmSide, EmZone[]> {
  const side = (a: Array<EmCell | null> | undefined): EmZone[] =>
    a && a.some((c) => c && c[0] > 0) ? [0, 1, 2].map((k) => zoneOf(a[k])) : ['none', 'none', 'none'];
  return { short: side(cells?.short), long: side(cells?.long) };
}
export const dayZonesOf = (rec: EntryRecord): Record<EmSide, EmZone[]> => zonesOfCells(rec.day);
export const h4ZonesOf = (rec: EntryRecord): Record<EmSide, EmZone[]> => zonesOfCells(rec.r240);

// ---- the colour rule: the 5-minute bar's third × the chase line ----
export function readMarks(m: EntryMarks | null | undefined, price: number | null | undefined): EmRead | null {
  const r = m && m.ranges && m.ranges['5m'];
  if (!m || !r || price == null) return null;
  const span = r.hi - r.lo;
  const pos = span > 0 ? Math.max(0, Math.min(1, (price - r.lo) / span)) : 0.5;
  const third = thirdOf(pos);
  const zone = ((m.zones || ({} as Record<EmSide, EmZone[]>))[m.side] || [])[third] || 'mid';
  const pv = m.pivot, k = m.chaseAtr == null ? CHASE_ATR : m.chaseAtr, atr = m.atr != null && m.atr > 0 ? m.atr : null;
  const past = pv ? gapPct(m.side, price, pv.price) : null;
  const pastAtr = pv && atr ? gapAtr(m.side, price, pv.price, atr) : null;
  const chased = pastAtr != null && pastAtr > k;
  const tag: EmZone = chased || zone === 'bad' ? 'bad' : zone === 'mid' ? 'mid' : 'good';
  const wrong = m.side === 'short' ? ' above' : ' below';
  const dist = pastAtr != null
    ? (Math.abs(pastAtr) < 0.1 ? 'at' : pastAtr > 0 ? emAtr(pastAtr) + ' ATR past' : emAtr(-pastAtr) + ' ATR' + wrong)
    : (Math.abs(past || 0) < 0.1 ? 'at' : past! > 0 ? emPct(past!) + ' past' : emPct(-past!) + wrong);   // no ATR yet: in %
  const at = !pv ? 'MV-5 has no ' + m.side + ' setup'
    : dist + ' the ' + emPx(pv.price) + ' turning point' + (chased ? ' — chasing' : '');
  const reason = EM_THIRD[third] + ' third of the 5-min range · ' + at + (pv && pv.stale ? ' (MV-5’s last read' + (pv.asOf ? ', ' + pv.asOf : '') + ')' : '') + dayLine(m.ranges, price);
  return { tag, third, zone, past, pastAtr, chased, reason };
}

/** " · the day's bottom third, 49% off its 5.87 high" — where the price sits in the day so far ('' without a day range) */
export function dayLine(R: EmRanges | null | undefined, price: number): string {
  const D = R && R.D;
  if (!D || !(D.hi > D.lo)) return '';
  const pos = Math.max(0, Math.min(1, (price - D.lo) / (D.hi - D.lo))), off = (D.hi - price) / D.hi * 100;
  return ' · the day’s ' + EM_THIRD[thirdOf(pos)]!.toLowerCase() + ' third, ' + (off < 1 ? 'at its ' + emPx(D.hi) + ' high' : emPct(off) + ' off its ' + emPx(D.hi) + ' high');
}

// ---- 5m / 15m / 4h low–high from 1-minute bars, the forming one included, and the day's ----
// The bars of the last 5 / 15 / 240 minutes up to the newest bar (a minute with no prints has no bar, so a thin or
// halted stock's window holds fewer); the day: the bars since 04:00 ET of the newest bar's session. `closed` (15m) is the
// window without the newest bar: what only this minute has covered draws dotted.
export function rangesFrom(bars: readonly EmBar[] | null | undefined): EmRanges | null {
  if (!bars || !bars.length) return null;
  const n = bars.length, last = bars[n - 1]!;
  const win = (mins: number, withClosed: boolean, fromSec?: number): EmRange | null => {
    const from = fromSec != null ? fromSec : last.time - (mins - 1) * 60;
    let i = n - 1;
    while (i > 0 && bars[i - 1]!.time >= from) i--;
    let lo = Infinity, hi = -Infinity, clo = Infinity, chi = -Infinity;
    for (let k = i; k < n; k++) {
      const b = bars[k]!;
      if (!(b.high >= b.low)) continue;
      if (b.low < lo) lo = b.low;
      if (b.high > hi) hi = b.high;
      if (k < n - 1) { if (b.low < clo) clo = b.low; if (b.high > chi) chi = b.high; }
    }
    if (!(hi >= lo)) return null;
    const out: EmRange = { lo, hi };
    if (withClosed && chi >= clo) out.closed = { lo: clo, hi: chi };
    return out;
  };
  const r5 = win(5, false);
  if (!r5) return null;
  const out: EmRanges = { '5m': r5 };
  const r15 = win(15, true), r4h = win(240, false), rD = win(0, false, Math.floor(sessionStartMs(last.time * 1000) / 1000));
  if (r15) out['15m'] = r15;
  if (r4h) out['4h'] = r4h;
  if (rD) out.D = rD;
  return out;
}

// ---- MV-5's turning point for the armed side, from its frame (the journal's late-vs-pivot setup) ----
const STUDIED: Record<EmSide, readonly string[]> = { long: ['higher_low', 'selling_stopped', 'base'], short: ['lower_high'] };
const fin = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export function pivotFrom(frame: any, side: EmSide, price: number | null | undefined, now: number): EmPivot | null {
  if (!frame || frame.warm === false || !Array.isArray(frame.objects)) return null;
  let best: { cls: string; tf: string; p: number; pivot: number; gap: number } | null = null;
  for (const o of frame.objects) {
    if (!o || !STUDIED[side].includes(o.class)) continue;
    if (!(o.state == null || o.state === 'forming' || o.state === 'confirmed')) continue;
    const p = fin(o.p), tf = Array.isArray(o.tfs) ? o.tfs.find((x: unknown) => typeof x === 'string') : undefined;
    if (p === null || !tf) continue;
    const pivot = fin(o.facts && o.facts.pivot) ?? (side === 'long' ? fin(o.priceLow) : fin(o.priceHigh));
    if (pivot === null || !(pivot > 0)) continue;
    const gap = price != null ? gapPct(side, price, pivot) : 0;
    if (!best || p > best.p || (p === best.p && gap > best.gap)) best = { cls: o.class, tf, p, pivot, gap };
  }
  if (!best) return null;
  const out: EmPivot = { price: best.pivot, setup: best.cls.replace(/_/g, ' '), tf: best.tf, p: best.p };
  const ts = fin(frame.ts);
  if (readState(ts, now) === 'last') { out.stale = true; out.asOf = etWhen(ts!, now).replace(/:\d\d$/, '') + ' ET'; }
  return out;
}

/** The marks for the armed side: the ranges and the ATR from the 1-minute bars, the turning point from MV-5's frame,
 *  the zones from his record; the tag and its reason filled in. */
export function entryMarksFor(side: EmSide, price: number | null, bars: readonly EmBar[] | null, frame: any, rec: EntryRecord, now: number): EntryMarks {
  const m: EntryMarks = {
    side, price, ranges: rangesFrom(bars), zones: zonesOf(rec), zonesDay: dayZonesOf(rec), zones4h: h4ZonesOf(rec),
    pivot: pivotFrom(frame, side, price, now), atr: atrFrom(bars, now), chaseAtr: CHASE_ATR,
  };
  const r = readMarks(m, price);
  if (r) { m.tag = r.tag; m.reason = r.reason; }
  return m;
}

/** The feed's answer (GET /api/journal/entry-record) as a record, or null when it is not one. */
export function parseRecord(x: any): EntryRecord | null {
  const cell = (c: any): EmCell | null => (Array.isArray(c) && c.length === 3 && c.every((v: unknown) => typeof v === 'number' && Number.isFinite(v)) ? [c[0], c[1], c[2]] : null);
  const side = (a: any): Array<EmCell | null> | null => (Array.isArray(a) && a.length === 3 ? a.map(cell) : null);
  if (!x || typeof x !== 'object') return null;
  const s = side(x.short), l = side(x.long);
  if (!s || !l || typeof x.trades !== 'number') return null;
  const pair = (o: any) => { const a = o && side(o.short), b = o && side(o.long); return a && b ? { short: a, long: b } : null; };
  return {
    span: typeof x.span === 'string' ? x.span : '', trades: x.trades,
    since15m: typeof x.since15m === 'string' ? x.since15m : null,
    short: s, long: l,
    chase: { short: cell(x.chase && x.chase.short), long: cell(x.chase && x.chase.long) },
    day: pair(x.day), r240: pair(x.r240), since240: typeof x.since240 === 'string' ? x.since240 : null,
  };
}
